(function () {
  const OPEN_EVENT = 'jjb:membership-profile-open';
  const SAVE_EVENT = 'jjb:membership-profile-save';
  let dialog;
  let form;
  let saveHandler = null;
  let scrollLockSnapshot = null;
  let viewportResizeHandler = null;
  let viewportScrollHandler = null;

  function updateDialogViewport() {
    if (!dialog || !dialog.open) return;
    const viewport = window.visualViewport;
    const height = Math.max(
      1,
      Number(viewport?.height) || window.innerHeight || document.documentElement.clientHeight,
    );
    const offsetTop = Number(viewport?.offsetTop) || 0;
    const width = Math.max(
      1,
      Number(viewport?.width) || window.innerWidth || document.documentElement.clientWidth,
    );
    dialog.style.setProperty(
      '--jjb-membership-profile-popup-viewport-height',
      `${height}px`,
    );
    dialog.style.setProperty(
      '--jjb-membership-profile-popup-center-y',
      `${offsetTop + height / 2}px`,
    );
    dialog.style.setProperty(
      '--jjb-membership-profile-popup-viewport-width',
      `${width}px`,
    );
  }

  function bindDialogViewport() {
    if (viewportResizeHandler) return;
    viewportResizeHandler = updateDialogViewport;
    viewportScrollHandler = updateDialogViewport;
    window.addEventListener('resize', viewportResizeHandler);
    window.visualViewport?.addEventListener('resize', viewportResizeHandler);
    window.visualViewport?.addEventListener('scroll', viewportScrollHandler);
    updateDialogViewport();
  }

  function unbindDialogViewport() {
    if (!viewportResizeHandler) return;
    window.removeEventListener('resize', viewportResizeHandler);
    window.visualViewport?.removeEventListener('resize', viewportResizeHandler);
    window.visualViewport?.removeEventListener('scroll', viewportScrollHandler);
    viewportResizeHandler = null;
    viewportScrollHandler = null;
    dialog?.style.removeProperty('--jjb-membership-profile-popup-viewport-height');
    dialog?.style.removeProperty('--jjb-membership-profile-popup-center-y');
    dialog?.style.removeProperty('--jjb-membership-profile-popup-viewport-width');
  }

  function lockPageScroll() {
    if (scrollLockSnapshot || !document.body) return;
    const scrollY = window.pageYOffset || window.scrollY || 0;
    const bodyStyle = document.body.style;
    scrollLockSnapshot = {
      scrollY,
      position: bodyStyle.position,
      top: bodyStyle.top,
      width: bodyStyle.width,
    };
    document.documentElement.classList.add('jjb-membership-profile-popup-open');
    bodyStyle.position = 'fixed';
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.width = '100%';
  }

  function unlockPageScroll() {
    if (!scrollLockSnapshot || !document.body) return;
    const snapshot = scrollLockSnapshot;
    const bodyStyle = document.body.style;
    bodyStyle.position = snapshot.position;
    bodyStyle.top = snapshot.top;
    bodyStyle.width = snapshot.width;
    document.documentElement.classList.remove('jjb-membership-profile-popup-open');
    scrollLockSnapshot = null;
    window.scrollTo(0, snapshot.scrollY);
  }

  function isTestMode() {
    let shopMode = '';
    try {
      shopMode = window.localStorage.getItem('_shop_mode') || '';
    } catch (_) {}
    return (
      window.location.hostname.includes('localhost') ||
      window.location.hostname.includes('127.0.0.1') ||
      window.location.hostname.includes('shopifypreview.com') ||
      shopMode === 'test'
    );
  }

  function getProfileEndpoint() {
    const prefix = isTestMode() ? '/apps/ai-test' : '/apps/ai';
    return `${prefix}/membership/profile`;
  }

  async function updateMembershipProfile(payload) {
    const response = await fetch(getProfileEndpoint(), {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
    });
    let result = null;
    try {
      result = await response.json();
    } catch (_) {}
    if (!response.ok || !result || result.success === false) {
      const error = new Error(
        result?.message || result?.errorMessage || 'Unable to save your profile.',
      );
      error.code = result?.code;
      error.status = response.status;
      throw error;
    }
    return result.data;
  }

  function applyCustomerName(payload) {
    const firstName = String(payload.firstName || '').trim();
    const lastName = String(payload.lastName || '').trim();
    const email = String(window.theme?.customerEmail || '').trim();
    const name = [firstName, lastName].filter(Boolean).join(' ') || email;
    if (window.theme) {
      window.theme.customerFirstName = firstName;
      window.theme.customerLastName = lastName;
      window.theme.customerName = name;
    }
    document
      .querySelectorAll('[data-membership-profile-name]')
      .forEach((node) => {
        node.textContent = name;
      });
  }

  function getCustomerDefaults() {
    const firstName = String(window.theme?.customerFirstName || '').trim();
    const lastName = String(window.theme?.customerLastName || '').trim();
    const name = String(window.theme?.customerName || '').trim();
    const parts = name ? name.split(/\s+/) : [];
    return {
      firstName: firstName || parts.shift() || '',
      lastName: lastName || parts.join(' '),
      email: String(window.theme?.customerEmail || '').trim(),
    };
  }

  function createDialog() {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'jjb-membership-profile-popup';
    dialog.setAttribute('aria-labelledby', 'jjb-membership-profile-popup-title');
    dialog.innerHTML = `
      <form class='jjb-membership-profile-popup__content' method='dialog'>
        <button class='jjb-membership-profile-popup__close' type='button' aria-label='Close profile editor'>
          <svg viewBox='0 0 16 16' aria-hidden='true' focusable='false'>
            <path d='m3 3 10 10M13 3 3 13' fill='none' stroke='currentColor' stroke-linecap='round' stroke-width='1.5'></path>
          </svg>
        </button>
        <fieldset class='jjb-membership-profile-popup__field-group'>
          <legend id='jjb-membership-profile-popup-title'>Name</legend>
          <div class='jjb-membership-profile-popup__name-fields'>
            <input name='firstName' autocomplete='given-name' placeholder='First name' aria-label='First name'>
            <input name='lastName' autocomplete='family-name' placeholder='Last name' aria-label='Last name'>
          </div>
        </fieldset>
        <fieldset class='jjb-membership-profile-popup__field-group'>
          <legend>Email address</legend>
          <input name='email' type='email' autocomplete='email' readonly aria-readonly='true' aria-label='Email address'>
        </fieldset>
        <p class='jjb-membership-profile-popup__email-note'>This email is used for sign-in and order updates.</p>
        <p class='jjb-membership-profile-popup__error' role='alert' aria-live='polite'></p>
        <button class='jjb-membership-profile-popup__save' type='submit'>Save</button>
      </form>
    `;
    document.body.appendChild(dialog);
    form = dialog.querySelector('form');
    dialog.querySelector('.jjb-membership-profile-popup__close').addEventListener(
      'click',
      () => dialog.close(),
    );
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      unbindDialogViewport();
      unlockPageScroll();
    });
    form.addEventListener('submit', handleSubmit);
    return dialog;
  }

  function showError(message) {
    const error = dialog?.querySelector('.jjb-membership-profile-popup__error');
    if (error) error.textContent = message || '';
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form) return;
    const submit = form.querySelector('.jjb-membership-profile-popup__save');
    const data = new FormData(form);
    const payload = {
      firstName: String(data.get('firstName') || '').trim(),
      lastName: String(data.get('lastName') || '').trim(),
    };
    showError('');
    submit.disabled = true;
    try {
      const save =
        typeof saveHandler === 'function'
          ? saveHandler
          : updateMembershipProfile;
      const savedProfile = await save(payload);
      const savedPayload = {
        firstName: String(savedProfile?.firstName ?? payload.firstName).trim(),
        lastName: String(savedProfile?.lastName ?? payload.lastName).trim(),
      };
      applyCustomerName(savedPayload);
      window.dispatchEvent(new CustomEvent(SAVE_EVENT, { detail: savedPayload }));
      dialog.close('saved');
      if (typeof window.toast === 'function') {
        window.toast('Profile updated');
      }
    } catch (error) {
      showError(error?.message || 'Unable to save your profile. Please try again.');
    } finally {
      submit.disabled = false;
    }
  }

  function open(options) {
    const values = Object.assign(getCustomerDefaults(), options || {});
    createDialog();
    form.elements.firstName.value = values.firstName || '';
    form.elements.lastName.value = values.lastName || '';
    form.elements.email.value = values.email || '';
    saveHandler = typeof values.onSave === 'function' ? values.onSave : saveHandler;
    showError('');
    lockPageScroll();
    dialog.showModal();
    bindDialogViewport();
    form.elements.firstName.focus({ preventScroll: true });
    window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: values }));
    return dialog;
  }

  function close() {
    if (dialog?.open) dialog.close();
  }

  window.jjbMembershipProfilePopup = {
    close,
    open,
    setSaveHandler(handler) {
      saveHandler = typeof handler === 'function' ? handler : null;
    },
  };
})();
